import { Link } from '@tanstack/react-router';
import { Bug, Hash, Link2, Maximize, MoreVertical, Wrench } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    getAgentTraceDestination,
    getAgentTracePath,
} from '@apps/web/lib/agent_chat_navigation';
import { toast } from '@apps/web/lib/app_notifications';

export type ChatOverflowMenuProps = Readonly<{
    readonly run: Readonly<{
        readonly id: string;
        readonly agentConversationId?: string | null;
        readonly organizationId: string;
        readonly mode: 'live' | 'sandbox';
    }> | null;
    readonly adminTools?: Readonly<{
        readonly debugPanelOpen: boolean;
        readonly onToggleDebugPanel: () => void;
    }>;
    readonly onEnterFullscreen?: () => void;
}>;

export function ChatOverflowMenu({
    run,
    adminTools,
    onEnterFullscreen,
}: ChatOverflowMenuProps): ReactNode {
    if (
        run === null &&
        adminTools === undefined &&
        onEnterFullscreen === undefined
    ) {
        return null;
    }

    async function copy(value: string, label: string): Promise<void> {
        try {
            await navigator.clipboard.writeText(value);
        } catch {
            toast.error(`Could not copy ${label}`, {
                persistNotification: true,
                notificationType: 'app_feedback',
            });
            return;
        }
        toast.success(`${label} copied`, {
            persistNotification: true,
            notificationType: 'app_feedback',
        });
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label="Chat actions"
                className="text-foreground/80 hover:bg-accent hover:text-foreground focus-visible:outline-agent-action data-[state=open]:bg-accent grid size-7 shrink-0 place-items-center rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
            >
                <MoreVertical aria-hidden="true" className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="min-w-44"
            >
                {onEnterFullscreen === undefined ? null : (
                    <DropdownMenuItem onSelect={onEnterFullscreen}>
                        <Maximize aria-hidden="true" className="size-4" />
                        Full screen
                    </DropdownMenuItem>
                )}
                {onEnterFullscreen !== undefined &&
                (run !== null || adminTools !== undefined) ? (
                    <DropdownMenuSeparator />
                ) : null}
                {run !== null && (
                    <Fragment>
                        <DropdownMenuItem
                            onSelect={() => {
                                const url = new URL(
                                    `/agent/runs/${encodeURIComponent(run.id)}`,
                                    window.location.origin,
                                );
                                url.searchParams.set(
                                    'organizationId',
                                    run.organizationId,
                                );
                                url.searchParams.set('mode', run.mode);
                                void copy(url.toString(), 'Agent URL');
                            }}
                        >
                            <Link2 aria-hidden="true" className="size-4" />
                            Copy agent URL
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => void copy(run.id, 'Run ID')}
                        >
                            <Hash aria-hidden="true" className="size-4" />
                            Copy run ID
                        </DropdownMenuItem>
                    </Fragment>
                )}
                {adminTools !== undefined && (
                    <Fragment>
                        {run !== null && (
                            <Fragment>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild={true}>
                                    <Link
                                        {...getAgentTraceDestination(
                                            run.id,
                                            run.agentConversationId ?? null,
                                        )}
                                    >
                                        <Wrench
                                            aria-hidden="true"
                                            className="size-4"
                                        />
                                        Go to agent trace
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onSelect={() =>
                                        void copy(
                                            new URL(
                                                getAgentTracePath(
                                                    run.id,
                                                    run.agentConversationId ??
                                                        null,
                                                ),
                                                window.location.origin,
                                            ).toString(),
                                            'Agent trace URL',
                                        )
                                    }
                                >
                                    <Link2
                                        aria-hidden="true"
                                        className="size-4"
                                    />
                                    Copy agent trace URL
                                </DropdownMenuItem>
                            </Fragment>
                        )}
                        <DropdownMenuItem
                            onSelect={adminTools.onToggleDebugPanel}
                        >
                            <Bug aria-hidden="true" className="size-4" />
                            {adminTools.debugPanelOpen
                                ? 'Hide debug'
                                : 'Show debug'}
                        </DropdownMenuItem>
                    </Fragment>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
